import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/Header';
import { colors, radius, shadows } from '../../styles/theme';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import { useFirmalar } from '../../hooks/useFirmalar';
import { formatTutarTR } from '../../utils/para';
import extractApiError from '../../utils/apiError';
import { logError } from '../../utils/logError';

// #3 Stopaj Raporu — seçilen dönemde türü bazında kesilen (withheld) stopaj.
// Backend GET /api/stopaj/ozet?baslangic=&bitis=&firma= (role-guarded: muhasebe/yonetim/admin).
// Salt-okunur rapor: { turler: [{stopaj_turu_kod, ad, stopaj_toplam, adet}], toplam }.

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const now = new Date();
const ayBasi = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
const bugun = now.toISOString().split('T')[0];

const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box',
};
const tdStyle = { padding: '8px 12px', fontSize: '14px', verticalAlign: 'middle' };

const StopajRaporuPage = () => {
  const toast = useToast();
  const { data: firmalarData } = useFirmalar();
  const firmalar = useMemo(
    () => (Array.isArray(firmalarData) ? firmalarData.filter((f) => f.aktif !== false) : []),
    [firmalarData]
  );

  const [turler, setTurler] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filtreler — URL'e bağlı (navigasyon + refresh sonrası yaşar, link paylaşılabilir).
  const [baslangic, setBaslangic] = useUrlState('sr_bas', ayBasi);
  const [bitis, setBitis] = useUrlState('sr_bit', bugun);
  const [firma, setFirma] = useUrlState('sr_firma', '');

  const fetchOzet = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (baslangic) params.set('baslangic', baslangic);
      if (bitis) params.set('bitis', bitis);
      if (firma) params.set('firma', firma);
      const res = await fetch(`${API}/api/stopaj/ozet?${params.toString()}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) {
        toast.error(extractApiError(data.detail));
        return;
      }
      setTurler(Array.isArray(data.turler) ? data.turler : []);
      setToplam(data.toplam ?? 0);
    } catch (err) {
      logError('Stopaj raporu yükleme hatası:', err);
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  }, [baslangic, bitis, firma, toast]);

  useEffect(() => { fetchOzet(); }, [fetchOzet]);

  if (loading && turler.length === 0) {
    return (
      <div>
        <Header title="Stopaj Raporu" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Stopaj Raporu" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Üst bar: başlık + filtreler */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: colors.textPrimary }}>🧾 Stopaj Raporu</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Başlangıç</label>
              <input type="date" value={baslangic} onChange={(e) => setBaslangic(e.target.value)}
                style={{ ...inputStyle, width: '160px' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Bitiş</label>
              <input type="date" value={bitis} onChange={(e) => setBitis(e.target.value)}
                style={{ ...inputStyle, width: '160px' }} />
            </div>
            <div style={{ minWidth: '180px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: colors.textSecondary, marginBottom: '4px', fontWeight: 'bold' }}>Firma</label>
              <select value={firma} onChange={(e) => setFirma(e.target.value)} style={inputStyle}>
                <option value="">Tümü</option>
                {firmalar.map((f) => (<option key={f.kod} value={f.kod}>{f.ad}</option>))}
              </select>
            </div>
          </div>
        </div>

        {/* KPI: toplam kesilen stopaj */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '24px',
        }}>
          <div style={{ background: colors.surface, borderRadius: radius.lg, borderLeft: `4px solid ${colors.primary}`, padding: '16px', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '11px', color: colors.textTertiary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
              Dönem Toplam Kesilen Stopaj
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
              {formatTutarTR(toplam)} <span style={{ fontSize: '12px', fontWeight: 500 }}>TL</span>
            </div>
          </div>
        </div>

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, boxShadow: shadows.sm, overflow: 'hidden' }}>
          {turler.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: colors.textTertiary }}>
              Bu döneme uyan stopaj kaydı bulunmuyor.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt }}>
                    {['Stopaj Türü', 'Kesilen Stopaj', 'Adet'].map((h) => (
                      <th key={h} style={{
                        padding: '10px 12px', fontSize: '11px', fontWeight: 600, color: colors.textTertiary,
                        letterSpacing: '0.04em', textAlign: h === 'Stopaj Türü' ? 'left' : 'right',
                        whiteSpace: 'nowrap', borderBottom: `2px solid ${colors.border}`,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {turler.map((t) => (
                    <tr key={t.stopaj_turu_kod} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{t.ad || t.stopaj_turu_kod}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {formatTutarTR(t.stopaj_toplam)} ₺
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.adet}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: colors.backgroundAlt, borderTop: `2px solid ${colors.border}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: '13px' }}>
                      Toplam ({turler.length} tür)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, fontSize: '14px', fontVariantNumeric: 'tabular-nums' }}>
                      {formatTutarTR(toplam)} ₺
                    </td>
                    <td style={{ padding: '10px 12px' }}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default StopajRaporuPage;
